import { takeEvery, put, select, take, call, fork } from 'redux-saga/effects';
import {
  selectAccountId,
  selectAccountMetaSynced,
  selectAccountUuid,
} from 'reducers/account/meta';
import { getUserEvents } from 'helpers/api';
import { reportError } from 'helpers/error-reporting';
import { getPropertyDisplayName } from 'utils';
import * as actionTypes from 'constants/actionTypes';
import { selectAccountUserEvent } from 'reducers/account/userEvents';
import { deleteAccountUserEventInStore } from 'actions/userEvents';
import * as firebase from 'sagas/firebase/lib';
import { throttleBy } from 'utils/sagas';
import { REPLACE_ACCOUNT_META } from 'constants/account/meta';

export function* replaceAccountUserEvents(res) {
  const { data } = res;

  /*
   * Though Firebase is the primary source of truth for User Event rollups,
   * some events have fallen through the cracks due to failed event batch
   * runs.  Generally, the API will catch those missed events, so we are here
   * reconciling the two rollup sources.
   *
   * This is intended to be temporary while the lambda stabilizes.
   */

  const accountMetaSynced = yield select(selectAccountMetaSynced);

  if (!accountMetaSynced) {
    yield take(REPLACE_ACCOUNT_META);
  }

  const accountId = yield select(selectAccountId);
  const accountUUID = yield select(selectAccountUuid);
  let apiEventNames;
  try {
    apiEventNames = yield call(getUserEvents, accountId, accountUUID);
  } catch (error) {
    apiEventNames = {};
    reportError(error);
  }
  const firebaseEventNames = Object.values(data || {});

  const mergedEventNames = Object.keys(apiEventNames).reduce(
    (acc, eventName) => {
      const firebaseEventName = firebaseEventNames.find(
        it => it.name === eventName
      );
      const { lastSeenTs } = apiEventNames[eventName];
      if (firebaseEventName) {
        return {
          ...acc,
          [firebaseEventName.id]: {
            ...firebaseEventName,
            lastSeenTs,
            source: 'firebase',
          },
        };
      }
      return {
        ...acc,
        [eventName]: {
          source: 'api',
          description: '',
          id: eventName,
          label: getPropertyDisplayName(eventName),
          name: eventName,
          lastSeenTs,
          showInUI: true,
        },
      };
    },
    {}
  );

  yield put({
    type: actionTypes.REPLACE_ACCOUNT_USER_EVENTS,
    payload: mergedEventNames,
  });
}

export function* replaceAccountUserEvent(res) {
  const { key: eventId, data } = res;

  if (yield select(selectAccountUserEvent, data.name)) {
    yield put(deleteAccountUserEventInStore(data.name));
  }

  yield put({
    type: actionTypes.REPLACE_ACCOUNT_USER_EVENT,
    payload: {
      eventId,
      userEvent: data,
    },
  });
}

function* flushUserEventToFirebase(action) {
  const { eventId } = action.payload;

  const accountId = yield select(selectAccountId);
  const userEvent = yield select(selectAccountUserEvent, eventId);

  yield userEvent
    ? call(
        firebase.set,
        `accounts/${accountId}/user-events/${eventId}`,
        userEvent
      )
    : call(firebase.remove, [`accounts/${accountId}/user-events/${eventId}`]);
}

export function* createUserEventInFirebase(userEvent) {
  const accountId = yield select(selectAccountId);
  return yield call(firebase.push, {
    [`accounts/${accountId}/user-events`]: userEvent,
  });
}

export default function* userEvents() {
  yield takeEvery(
    actionTypes.CREATE_USER_EVENT_IN_FIREBASE,
    createUserEventInFirebase
  );

  yield fork(
    throttleBy,
    'eventId',
    500,
    actionTypes.FLUSH_USER_EVENT_TO_FIREBASE,
    flushUserEventToFirebase
  );
}
